import { type BrandName } from "@elit-hair/brands";
import { getLocalStorage, saveLocalStorage } from "@elit-hair/elit-tools";
import { DEFAULT_COLOR_SCHEMES, THEMES } from "@elit-hair/elit-ui";
import { createContext, useContext, useEffect, useState } from "react";

import { DASHBOARD_VARIANTS } from "@/constants/dashboard-variants";
import { DATE_FORMAT, TIME_FORMAT } from "@/constants/date-time";
import { SETTINGS_KEYS } from "@/constants/settings-keys";
import { SOCKET_NAMESPACES } from "@/pages/calls/utils/transcript-socket";
import { DEFAULT_DASHBOARD_LAYOUT } from "@/pages/home/components/dashboard/constants";
import type { StoredDashboardLayout } from "@/pages/home/components/role-dashboard/types/dashboard-layout.types";
import { DEFAULT_SETTING_LIST_CONFIG, resolveSettingListConfig } from "@/pages/lists/utils/setting-list-config.utils";
import { useCurrentSettings, useUpdateCurrentConfig } from "@/queries/setting.queries";
import { soundService } from "@/services/soundService";
import { SettingListConfig } from "@/types/setting-list-config.types";

import { useSocket } from "../hooks/useSocket";

export type CustomLink = {
    id: string;
    label: string;
    url: string;
    iconName: string | null;
};

export type PinnedTeamMembersPreset = {
    id: string;
    name: string;
    memberIds: string[];
};

// If you add new settings, don't forget to add them in the DTO for the remote-config in backend
// (e.g. services/call-svc/src/dtos/remote-config.dto.ts) so they can be stored and returned by the API.
export type SettingsDefinition = {
    THEME: THEMES;
    COLOR_SCHEME: string;
    LANGUAGE: string;
    DATE_FORMAT: DATE_FORMAT;
    TIME_FORMAT: TIME_FORMAT;
    DARWIN_AI_ENABLED: boolean;
    AI_ANALYSIS_ENABLED: boolean;
    DIALER_VOLUME: number;
    SELECTED_BRAND: BrandName;
    HAS_SEEN_TOUR: boolean;
    SIDEBAR_COLLAPSED: boolean;
    OUTGOING_CALL_MELODY_MUTE: boolean;
    DASHBOARD_HIDDEN_MAP: Record<string, boolean>;
    DASHBOARD_COLUMN_LAYOUT: Record<string, string[]>;
    PINNED_TEAM_MEMBERS: string[];
    PINNED_TEAM_MEMBERS_PRESETS: PinnedTeamMembersPreset[];
    PINNED_TEAM_MEMBERS_CURRENT_PRESET: string | null;
    DASHBOARD_PINNED_MEMBERS_PRESETS: string[];
    DASHBOARD_QUALITY_WATCH_PRESETS: string[];
    QUALITY_WATCH_PRESETS: unknown[];
    QUALITY_WATCH_CURRENT_PRESET: string | null;
    CUSTOM_LINKS: CustomLink[];
    /** Department keys shown in Setting capacities (e.g. ["dach", "prp", "fr", "es", "uk"]). */
    PRIORITY_REPORT_DEPARTMENTS: string[];
    CALENDAR_CAPACITIES_VISIBLE: string[];
    SELECTED_DASHBOARD: DASHBOARD_VARIANTS;
    SETTING_LIST: SettingListConfig;
    HOME_DASHBOARD_LAYOUT: StoredDashboardLayout;
};

export const defaultSettings: SettingsDefinition = {
    THEME: THEMES.LIGHT,
    COLOR_SCHEME: DEFAULT_COLOR_SCHEMES[0].value,
    LANGUAGE: "en",
    DATE_FORMAT: DATE_FORMAT.DD_MM_YYYY,
    TIME_FORMAT: TIME_FORMAT.TWENTY_FOUR_HOURS,
    DARWIN_AI_ENABLED: false,
    AI_ANALYSIS_ENABLED: false,
    DIALER_VOLUME: 0,
    SELECTED_BRAND: "elithair",
    HAS_SEEN_TOUR: false,
    SIDEBAR_COLLAPSED: false,
    OUTGOING_CALL_MELODY_MUTE: false,
    DASHBOARD_HIDDEN_MAP: {},
    DASHBOARD_COLUMN_LAYOUT: DEFAULT_DASHBOARD_LAYOUT,
    PINNED_TEAM_MEMBERS: [],
    PINNED_TEAM_MEMBERS_PRESETS: [],
    PINNED_TEAM_MEMBERS_CURRENT_PRESET: null,
    DASHBOARD_PINNED_MEMBERS_PRESETS: [],
    DASHBOARD_QUALITY_WATCH_PRESETS: [],
    QUALITY_WATCH_PRESETS: [],
    QUALITY_WATCH_CURRENT_PRESET: null,
    CUSTOM_LINKS: [],
    PRIORITY_REPORT_DEPARTMENTS: [],
    CALENDAR_CAPACITIES_VISIBLE: [],
    SELECTED_DASHBOARD: DASHBOARD_VARIANTS.ROLE_DEFAULT,
    SETTING_LIST: DEFAULT_SETTING_LIST_CONFIG,
    HOME_DASHBOARD_LAYOUT: {},
};

export const SettingsContext = createContext<{
    currentSettings: SettingsDefinition;
    setValue: (key: SETTINGS_KEYS, value: SettingsDefinition[SETTINGS_KEYS]) => void;
}>({
    currentSettings: defaultSettings,
    setValue: () => {},
});

const loadLocalSettings = (): SettingsDefinition => {
    const settings = { ...defaultSettings };

    (Object.keys(defaultSettings) as Array<SETTINGS_KEYS>).forEach((key) => {
        const value = getLocalSetting(key);
        if (value !== null && value !== undefined) {
            (settings as Record<SETTINGS_KEYS, SettingsDefinition[SETTINGS_KEYS]>)[key] = value;
        }
    });

    return settings;
};

export const getLocalSetting = <T extends SettingsDefinition[SETTINGS_KEYS]>(key: SETTINGS_KEYS) => {
    return getLocalStorage<T>(key, defaultSettings[key] as T);
};

// Socket events
export type SettingsSocketEvents = {
    "config:updated": {
        adminUserId: string;
        config: SettingsDefinition;
        targetUserId: string;
        timestamp: string;
    };
};

const isSettingsDefinitionKey = (key: string): key is keyof SettingsDefinition => {
    return key in defaultSettings;
};

export const SettingsProvider = ({ children }: { children: React.ReactNode }) => {
    const { data: currentSettingsResponse } = useCurrentSettings();
    const { mutateAsync: updateCurrentConfig } = useUpdateCurrentConfig();

    const [currentSettings, setCurrentSettings] = useState<SettingsDefinition>(loadLocalSettings());

    // Parse partial config from backend (Some keys are undefined).
    // Start from defaults so a previous session's localStorage (not user-scoped) cannot leak
    // e.g. HOME_DASHBOARD_LAYOUT into another account on the same browser.
    const parsePartialConfig = (config: Partial<SettingsDefinition> & Record<string, unknown>): SettingsDefinition => {
        const newSettings: SettingsDefinition = {
            ...defaultSettings,
        };

        // Avoid assigning null from backend; ignore unknown/deprecated remote-config keys.
        Object.keys(config).forEach((key) => {
            if (key === SETTINGS_KEYS.SETTING_LIST || !isSettingsDefinitionKey(key)) {
                return;
            }

            const rawValue = config[key];
            if (rawValue === null || rawValue === undefined) {
                return;
            }

            Object.assign(newSettings, { [key]: rawValue });
            saveLocalStorage(key, rawValue);
        });

        // Clear local cache for keys the remote does not set, so the next cold load matches defaults.
        (Object.keys(defaultSettings) as Array<SETTINGS_KEYS>).forEach((key) => {
            if (key === SETTINGS_KEYS.SETTING_LIST) {
                return;
            }
            if (config[key] !== null && config[key] !== undefined) {
                return;
            }
            saveLocalStorage(key, defaultSettings[key]);
        });

        const settingList = resolveSettingListConfig(config);
        newSettings[SETTINGS_KEYS.SETTING_LIST] = settingList;
        saveLocalStorage(SETTINGS_KEYS.SETTING_LIST, settingList);
        return newSettings;
    };

    const { useSocketCallback } = useSocket<SettingsSocketEvents>({
        namespace: SOCKET_NAMESPACES.REMOTE_CONFIG,
    });
    useSocketCallback("config:updated", (config) => {
        const newConfig = parsePartialConfig(config.config);
        setCurrentSettings(newConfig);
    });

    useEffect(() => {
        if (currentSettingsResponse) {
            const newConfig = parsePartialConfig(currentSettingsResponse);
            setCurrentSettings(newConfig);
        }
    }, [currentSettingsResponse]);

    useEffect(() => {
        soundService.updateVolumeFromValue(Number(currentSettings.DIALER_VOLUME));
    }, [currentSettings.DIALER_VOLUME]);

    const setValue = (key: SETTINGS_KEYS, value: SettingsDefinition[SETTINGS_KEYS]) => {
        saveLocalStorage(key, value);
        setCurrentSettings((prev) => ({ ...prev, [key]: value }));
        updateCurrentConfig({ [key]: value });
    };

    return <SettingsContext.Provider value={{ currentSettings, setValue }}>{children}</SettingsContext.Provider>;
};

export const useSettings = () => {
    return useContext(SettingsContext);
};
